import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", subtitle: "", image_path: "", cta_label: "Comprar agora", cta_link: "/", order: 0, active: true };

export default function AdminBanners() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/banners").then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "banners");
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((e) => ({ ...e, image_path: data.path }));
    } catch (e) {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!edit.image_path) {
      toast.error("Envie uma imagem para o banner");
      return;
    }
    const payload = { ...edit, order: Number(edit.order) || 0 };
    try {
      if (edit.id) await api.put(`/admin/banners/${edit.id}`, payload);
      else await api.post("/admin/banners", payload);
      toast.success("Banner salvo");
      setEdit(null);
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const del = async (b) => {
    if (!window.confirm("Excluir banner?")) return;
    await api.delete(`/admin/banners/${b.id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline-label">Home</div>
          <h1 className="font-display text-4xl text-white tracking-tighter mt-3">Banners</h1>
        </div>
        <Button onClick={() => setEdit({ ...empty })} data-testid="new-banner-btn" className="bg-white text-black hover:bg-zinc-200 uppercase tracking-widest text-xs h-11 px-5">
          <Plus size={14} className="mr-2" /> Novo banner
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((b) => (
          <div key={b.id} className="border border-white/10 bg-[#0A0A0C] overflow-hidden" data-testid={`banner-card-${b.id}`}>
            <div className="relative aspect-[16/9] bg-black">
              {b.image_path && <img src={fileUrl(b.image_path)} alt={b.title} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="font-display text-lg">{b.title}</div>
              <div className="text-xs text-zinc-500 truncate">{b.subtitle}</div>
              <div className="flex items-center justify-between mt-3">
                <span className={`text-[11px] uppercase tracking-wide ${b.active ? "text-emerald-400" : "text-zinc-500"}`}>{b.active ? "Ativo" : "Inativo"}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEdit({ ...b })} className="bg-transparent border-white/15 text-white hover:bg-white/5 h-8 px-3"><Pencil size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(b)} className="bg-transparent border-red-500/40 text-red-300 hover:bg-red-500/10 h-8 px-3"><Trash2 size={14} /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="col-span-full text-center text-zinc-500 py-16">Nenhum banner cadastrado.</p>}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="bg-[#0A0A0C] border-white/10 text-white max-w-xl">
          <DialogHeader><DialogTitle>{edit?.id ? "Editar banner" : "Novo banner"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="aspect-[16/9] border border-dashed border-white/15 bg-black grid place-items-center overflow-hidden">
                {edit.image_path ? (
                  <img src={fileUrl(edit.image_path)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <button type="button" onClick={() => fileRef.current?.click()} className="text-zinc-400 hover:text-white text-sm">
                    <Upload size={18} className="mx-auto mb-2" /> Enviar imagem
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-transparent border-white/15 text-white hover:bg-white/5 w-full h-10">
                {uploading ? "Enviando..." : edit.image_path ? "Trocar imagem" : "Enviar imagem"}
              </Button>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400">Título *</label>
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="bg-[#050505] border-white/15 mt-1 h-11" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400">Ordem</label>
                  <Input type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} className="bg-[#050505] border-white/15 mt-1 h-11" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-zinc-400">Subtítulo</label>
                  <Textarea value={edit.subtitle} onChange={(e) => setEdit({ ...edit, subtitle: e.target.value })} rows={2} className="bg-[#050505] border-white/15 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400">Texto do botão</label>
                  <Input value={edit.cta_label} onChange={(e) => setEdit({ ...edit, cta_label: e.target.value })} className="bg-[#050505] border-white/15 mt-1 h-11" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400">Link do botão</label>
                  <Input value={edit.cta_link} onChange={(e) => setEdit({ ...edit, cta_link: e.target.value })} className="bg-[#050505] border-white/15 mt-1 h-11" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-300">Ativo</span>
                <Switch checked={!!edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="bg-transparent border-white/15 text-white">Cancelar</Button>
            <Button onClick={save} className="bg-white text-black hover:bg-zinc-200" data-testid="save-banner-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
